import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { setSettings } from '../../stores/WorkspaceStore'
import { ServiceFactory } from '../factories/ServiceFactory'
import AuthenticationController from './AuthenticationController'
import MainMenuController from './MainMenuController'
import RoomController from './RoomController'
import FirstTimeProfileSetup from '../../components/FirstTimeProfileSetup'
import { TermsGateView, isGuestUserId, useTermsGateBlocking } from '../../components/consent/TermsGate'
import OnboardingWizard from '../../components/Onboarding/OnboardingWizard'
import { useOnboardingGate } from '../../components/Onboarding/useOnboardingGate'
import { RootState } from '../../stores'

/**
 * Main application controller that manages the overall app flow
 * and coordinates between different controllers
 */
const AppController: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const authenticated = useAppSelector((state: RootState) => state.user.authenticated)
  const loggedIn = useAppSelector((state: RootState) => state.user.loggedIn)
  const roomJoined = useAppSelector((state: RootState) => state.room.roomJoined)
  const loadingGame = useAppSelector((state: RootState) => state.game.loadingGame)
  const termsGateBlocking = useTermsGateBlocking()
  const userData = useAppSelector((state: RootState) => state.user.userData)
  const userId = userData?.id ?? null
  // SC-438: checked after the Terms screen, and never for guests or before the email code.
  const onboardingGate = useOnboardingGate(
    authenticated && !termsGateBlocking && userData?.emailVerified !== false && !isGuestUserId(userId),
    userId
  )

  const workspaceService = ServiceFactory.getInstance().getWorkspaceService()
  const authService = ServiceFactory.getInstance().getAuthService()
  const [isInitializing, setIsInitializing] = useState(true)
  const [isSessionRestored, setIsSessionRestored] = useState(false)

  // Wait for session restoration to complete
  useEffect(() => {
    const waitForSessionRestoration = async () => {
      try {
        // Race session check against an 8-second timeout so a slow/no connection
        // never hangs the app on the HTML preloader indefinitely.
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, 8000))
        await Promise.race([authService.isAuthenticated(), timeout])
        setIsSessionRestored(true)
      } catch (error) {
        setIsSessionRestored(true) // Continue anyway
      }
    }

    waitForSessionRestoration()
  }, [authService])

  // Load workspace settings on mount (for all users, including unauthenticated)
  useEffect(() => {
    const loadWorkspaceSettings = async () => {
      try {
        // Race workspace load against an 8-second timeout to prevent hanging on
        // slow/unavailable connections. WorkspaceService falls back to defaults on error.
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, 8000))
        await Promise.race([workspaceService.ensureSettingsLoaded(), timeout])
        const settings = await workspaceService.getSettings()
        if (settings) {
          dispatch(setSettings(settings))
        }
      } catch (error) {
        // Silent error handling for production
      } finally {
        setIsInitializing(false)
      }
    }

    loadWorkspaceSettings()
  }, [dispatch, workspaceService])

  // Remove preloader when AppController is ready (and not logged in, as App.tsx handles that)
  useEffect(() => {
    if (!isInitializing && isSessionRestored && !loggedIn) {
      const preloader = document.getElementById('initial-preloader')
      if (preloader && preloader.style.opacity !== '0') {
        preloader.style.transition = 'opacity 0.5s ease'
        preloader.style.opacity = '0'
        setTimeout(() => {
          preloader.remove()
        }, 500)
      }
    }
  }, [isInitializing, isSessionRestored, loggedIn])

  const handleJoinPublicLobby = () => {
    // This will be handled by the RoomController
  }

  // Show loading while initializing or waiting for session restoration
  // Show loading while initializing or waiting for session restoration
  if (isInitializing || !isSessionRestored) {
    return null // Return null to let the HTML preloader show through
  }

  // Determine which controller to show based on app state
  if (loggedIn) {
    // User is logged in and in a room - show game UI
    return <div>Game UI</div>
  } else if (roomJoined && authenticated) {
    // User is authenticated and joined a room but not logged in yet
    return <div>Joining room...</div>
  } else if (authenticated) {
    // SC-360: the Terms check (then the Terms screen, if due) replaces room selection here,
    // inside this component, so the controller itself stays mounted through it.
    if (termsGateBlocking) {
      return <TermsGateView />
    }
    // SC-438: the menu (and its landing redirect) waits for the onboarding answer, and the
    // wizard replaces it while onboarding is active.
    if (onboardingGate === 'checking') {
      return null
    }
    if (onboardingGate === 'wizard') {
      return <OnboardingWizard />
    }
    // User is authenticated but not in a room - show room selection with first-time profile setup
    return (
      <>
        <FirstTimeProfileSetup />
        <RoomController />
      </>
    )
  } else {
    // User is not authenticated - show authentication
    return <AuthenticationController />
  }
}

export default AppController 